/**
 * A way to navigate that outlives the component that asked for it.
 *
 * `useNavigate` gives you a function tied to the component that called it, and the shell is
 * re-rendered per screen — so anything that navigates and then keeps working across that
 * navigation, like the walkthrough, is holding a function from a component that has since
 * unmounted. It went quiet after the first step for exactly this reason.
 *
 * `App` sits above `Routes` and never unmounts, so the navigator registered from there stays
 * good for the life of the session.
 */

import { useEffect } from "react";
import { useNavigate, type NavigateFunction } from "react-router-dom";

let navigator: NavigateFunction | null = null;

/** Called once, from a component above the routes. */
export function useRegisterNavigator(): void {
  const navigate = useNavigate();
  useEffect(() => {
    navigator = navigate;
  }, [navigate]);
}

export function navigateTo(to: string): void {
  if (!navigator) {
    console.warn(`[nav] asked to go to ${to} before a navigator was registered`);
    return;
  }
  navigator(to);
}
